import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import { PromotionBanner } from "@src/appV2/redesign/system/ui-components";

export interface ShiftHistoryBannerCardData {
  id: string;
  sortDate: Date;
  title: string;
  subtitle: string;
  icon: "clock-success-with-dollar-colored" | "clock-error-with-exclamation-colored";
  variant: "success" | "error";
  ctaButtonText?: string;
  ctaButtonIsLoading?: boolean;
  errorMessage?: string;
  onClick?: () => void;
}

function getBannerSx(errorMessage?: string) {
  const hasErrorMessage = isDefined(errorMessage);

  return (theme: Theme) => ({
    width: "100%",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.08)",
    alignItems: "stretch",
    "& *": {
      whiteSpace: "normal",
      wordWrap: "break-word",
    },
    "& .MuiCardContent-root": {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
    },
    "& .MuiCardContent-root > .MuiButton-root": {
      marginTop: "auto",
    },
    "& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type": {
      display: "grid",
      gridTemplateColumns: hasErrorMessage ? "auto 1fr auto" : "auto 1fr",
      columnGap: hasErrorMessage ? 0 : theme.spacing(4),
      rowGap: theme.spacing(3),
      alignItems: "center",
      ...(hasErrorMessage && {
        "&::before": {
          content: JSON.stringify(`\u201C${errorMessage}`),
          gridRow: 3,
          gridColumn: "1 / 3",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          marginBottom: theme.spacing(2),
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.intent!.error.text,
          fontWeight: 500,
        },
        "&::after": {
          content: '"\u201D"',
          gridRow: 3,
          gridColumn: 3,
          marginBottom: theme.spacing(2),
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.intent!.error.text,
          fontWeight: 500,
        },
      }),
    },
    "& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type > .MuiStack-root": {
      display: "contents",
    },
    "& .MuiTypography-body1": {
      fontSize: theme.typography.h6.fontSize,
      lineHeight: theme.typography.h6.lineHeight,
      ...(hasErrorMessage && { marginLeft: theme.spacing(4) }),
    },
    "& .MuiTypography-body2": {
      gridColumn: "1 / -1",
    },
  });
}

export function ShiftHistoryBannerCard(props: { data: ShiftHistoryBannerCardData }) {
  const { data } = props;

  return (
    <PromotionBanner
      title={data.title}
      subtitle={data.subtitle}
      icon={data.icon}
      borderVariant="none"
      backgroundColor={(theme) =>
        data.variant === "success"
          ? theme.palette.payment!.background
          : theme.palette.intent!.error.gradient!
      }
      titleColor={(theme) =>
        data.variant === "success" ? theme.palette.payment!.text : theme.palette.intent!.error.text
      }
      {...(isDefined(data.ctaButtonText) && {
        ctaButtonText: data.ctaButtonText,
        ctaButtonVariant: "outlined" as const,
        ctaButtonIsLoading: data.ctaButtonIsLoading,
      })}
      sx={getBannerSx(data.errorMessage)}
      {...(isDefined(data.onClick) && { onClick: data.onClick })}
    />
  );
}
